import { toSignal } from '@angular/core/rxjs-interop';
import { computed, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SelectionService } from '@portal/app/shared/services/selection.service';

import {
  FeatureConfigV2ControllerService,
  FeatureConfigResponseDTO
} from '@libs/api';
import { isCrossChannelDashboards } from '@portal/app/shared/constants/constants';

const hideXcdExportFlag =
  'HIDE_XCD_EXPORT' as FeatureConfigResponseDTO.FeatureNameEnum;

@Injectable({
  providedIn: 'root'
})
export class FeatureConfigService {
  private dataSubject = new BehaviorSubject<FeatureConfigResponseDTO[]>([]);
  private loadedSubject = new BehaviorSubject<boolean>(false);

  data$ = this.dataSubject.asObservable();
  /** Emits true once the initial fetch has settled (also on failure, so consumers never hang). */
  loaded$ = this.loadedSubject.asObservable();
  private dataSignal = toSignal(this.data$, { initialValue: [] });

  constructor(
    private readonly featureConfigV2ControllerService: FeatureConfigV2ControllerService,
    private readonly selectionService: SelectionService
  ) {
    this.fetchData();
  }

  fetchData() {
    const { clientId, brandId } = this.selectionService.getClientIdAndBrandId();
    return this.featureConfigV2ControllerService
      .getAllFeatureConfigs(clientId, brandId)
      .subscribe({
        next: (data: FeatureConfigResponseDTO[]) => {
          this.setData(data);
          this.loadedSubject.next(true);
        },
        error: () => this.loadedSubject.next(true)
      });
  }

  setData(data: FeatureConfigResponseDTO[]): void {
    this.dataSubject.next(data);
  }

  getData(): FeatureConfigResponseDTO[] {
    return this.dataSubject.getValue();
  }

  // /v2/feature-config already evaluated each flag for this caller and tenant (PMDEV-10013).
  private checkFeatureEnabled(
    items: FeatureConfigResponseDTO[],
    key: FeatureConfigResponseDTO.FeatureNameEnum
  ): boolean {
    return items.some((item) => item.featureName === key);
  }

  isFeatureEnabled(key: FeatureConfigResponseDTO.FeatureNameEnum): boolean {
    return this.checkFeatureEnabled(this.dataSubject.getValue(), key);
  }

  isXcdExportHidden(literalId: string): boolean {
    return (
      isCrossChannelDashboards(literalId) &&
      this.isFeatureEnabled(hideXcdExportFlag)
    );
  }

  isFeatureEnabledSignal(key: FeatureConfigResponseDTO.FeatureNameEnum) {
    return computed(() => this.checkFeatureEnabled(this.dataSignal(), key));
  }

  /**
   * Gets the visibility level for a specific feature
   * @param key The feature name enum
   * @returns The visibility enum value if feature exists, undefined otherwise
   */
  getFeatureVisibility(
    key: FeatureConfigResponseDTO.FeatureNameEnum
  ): FeatureConfigResponseDTO.VisibilityEnum | undefined {
    const featureConfig = this.dataSubject
      .getValue()
      .find((item) => item.featureName === key);

    return featureConfig?.visibility as FeatureConfigResponseDTO.VisibilityEnum;
  }
}
